package com.klantenstop.web.components.curatedcities

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.table.DataTable
import com.klantenstop.web.components.table.TableColumnDef
import com.klantenstop.web.data.models.domain.CuratedCity
import com.klantenstop.web.data.models.domain.Province
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.components.widgets.IconButton
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Instant
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

/**
 * Renders curated cities in a single DataTable, grouped by province via sub-header rows
 * (order pre-sorted by province display name, see [groupedCuratedCities]).
 *
 * @param groupedCuratedCities Curated cities grouped by province, keys expected pre-sorted
 * @param onNavigateToEdit Callback when edit button is clicked
 * @param onRequestDelete Callback when delete button is clicked
 */
@Composable
fun CuratedCityTable(
    groupedCuratedCities: Map<Province, List<CuratedCity>>,
    onNavigateToEdit: (String) -> Unit,
    onRequestDelete: (CuratedCity) -> Unit
) {
    DataTable(
        items = groupedCuratedCities.values.flatten(),
        columns = getCuratedCityColumns(onNavigateToEdit, onRequestDelete),
        emptyMessage = "Geen steden gevonden.",
        groupKeyOf = { it.province.displayName },
        groupOrder = groupedCuratedCities.keys.map { it.displayName }
    )
}

private fun getCuratedCityColumns(
    onNavigateToEdit: (String) -> Unit,
    onRequestDelete: (CuratedCity) -> Unit
): List<TableColumnDef<CuratedCity>> = listOf(
    TableColumnDef(
        heading = "Naam",
        resizable = true,
        comparator = compareBy { it.name },
        cellContent = { curatedCity ->
            SpanText(
                text = curatedCity.name,
                modifier = Modifier.fontSize(14.px).color(MaterialColorVars.OnSurface.value())
            )
        }
    ),
    TableColumnDef(
        heading = "Slug",
        resizable = true,
        wrapContent = true,
        comparator = compareBy { it.slug },
        cellContent = { curatedCity ->
            Text(curatedCity.slug)
        }
    ),
    TableColumnDef(
        heading = "Uitgelicht",
        resizable = false,
        wrapContent = true,
        comparator = compareBy { it.isFeatured },
        cellContent = { curatedCity ->
            if (curatedCity.isFeatured) {
                DsMaterialSymbols(
                    icon = "star",
                    modifier = Modifier.fontSize(18.px).color(MaterialColorVars.Primary.value())
                )
            }
        }
    ),
    TableColumnDef(
        heading = "Aangemaakt",
        resizable = false,
        wrapContent = true,
        comparator = compareBy { it.createdAt },
        cellContent = { curatedCity ->
            Text(formatDate(curatedCity.createdAt))
        }
    ),
    TableColumnDef(
        heading = "",
        resizable = false,
        wrapContent = true,
        comparator = null,
        cellContent = { curatedCity ->
            CuratedCityActions(
                onEdit = { onNavigateToEdit(curatedCity.id) },
                onDelete = { onRequestDelete(curatedCity) }
            )
        }
    )
)

@Composable
private fun CuratedCityActions(
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(4.px, Alignment.End)
    ) {
        IconButton(
            size = ButtonSize.SM,
            type = ButtonType.Button,
            onClick = { onEdit() }
        ) {
            DsMaterialSymbols(icon = "edit", modifier = Modifier.fontSize(18.px))
        }
        IconButton(
            size = ButtonSize.SM,
            type = ButtonType.Button,
            onClick = { onDelete() }
        ) {
            DsMaterialSymbols(
                icon = "delete",
                modifier = Modifier.fontSize(18.px).color(MaterialColorVars.Error.value())
            )
        }
    }
}

private fun formatDate(instant: Instant): String {
    return instant.toString().substringBefore("T")
}
